import { useMemo, useState } from 'react';
import type { AiReportResult, ReportChart } from '@order-pilot/shared';
import { Button } from '@/components/Button';
import { BarList } from '@/components/charts/BarList';
import { DonutChart } from '@/components/charts/DonutChart';
import { TrendChart } from '@/components/charts/TrendChart';
import { getErrorMessage } from '@/lib/api';
import { formatPosDate } from '@/lib/format';
import { useSaveReport } from './api';
import { columnTotal, formatValue, isSummable, numeric, summarise } from './summary';

const CHARTS: Exclude<ReportChart, 'table'>[] = ['bar', 'line', 'pie'];

function Chart({ result, kind }: { result: AiReportResult; kind: Exclude<ReportChart, 'table'> }) {
  const dim = result.columns.find((c) => c.kind === 'dimension')!;
  const measure = result.columns.find((c) => c.kind !== 'dimension')!;
  const items = result.rows.map((row, i) => ({
    key: `${i}-${String(row[dim.key])}`,
    label: String(row[dim.key]),
    value: numeric(row[measure.key]),
    display: formatValue(row[measure.key], measure, result.currency),
  }));
  const title = `${measure.label} by ${dim.label.toLowerCase()}`;
  if (kind === 'line') {
    return <TrendChart points={items.map((p) => ({ ...p, fullLabel: p.label }))} title={title} valueHeader={measure.label} />;
  }
  if (kind === 'pie') {
    const total = isSummable(measure.key) ? formatValue(columnTotal(result.rows, measure), measure, result.currency) : '';
    return <DonutChart slices={items} label={title} centerValue={total} centerLabel={total ? 'Total' : ''} />;
  }
  return <BarList items={items} label={title} />;
}

/**
 * One answer: title and period, a plain-English line written from the
 * figures, a chart the reader can switch, the table with its total, and the
 * actions. `saved` reports were opened from the list and cost nothing.
 */
export function ReportResult({
  result,
  question,
  saved,
  onFollowUp,
}: {
  result: AiReportResult;
  question?: string;
  saved?: boolean;
  onFollowUp?: () => void;
}) {
  const [chart, setChart] = useState<ReportChart>(result.spec.chart === 'table' ? 'bar' : result.spec.chart);
  const [naming, setNaming] = useState(false);
  const [title, setTitle] = useState(result.spec.title);
  const save = useSaveReport();
  const dim = result.columns.find((c) => c.kind === 'dimension');
  const canChart = !!dim && result.rows.length > 0;
  const summary = useMemo(() => summarise(result), [result]);

  return (
    <article className="card overflow-hidden">
      <div className="flex flex-col gap-2.5 px-5 pt-5">
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-[180px] flex-1">
            <h2 className="text-base font-semibold text-slate-900">{result.spec.title}</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {result.from === result.to ? formatPosDate(result.from) : `${formatPosDate(result.from)} – ${formatPosDate(result.to)}`}
            </p>
          </div>
          {canChart ? (
            <div className="flex gap-0.5 rounded-lg border border-slate-200 p-0.5" role="group" aria-label="Chart type">
              {CHARTS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setChart(k)}
                  aria-pressed={chart === k}
                  className={`rounded-md px-2.5 py-1 text-xs capitalize ${chart === k ? 'bg-slate-100 font-medium text-slate-900' : 'text-slate-500 hover:text-slate-900'}`}
                >
                  {k}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        {summary ? (
          <p className="flex items-start gap-2 text-sm leading-relaxed text-slate-800">
            <span aria-hidden="true" className="text-brand-600">✦</span>
            <span>{summary}</span>
          </p>
        ) : null}
      </div>

      {canChart && chart !== 'table' ? (
        <div className="px-5 pt-4">
          <Chart result={result} kind={chart} />
        </div>
      ) : null}

      {result.rows.length > 0 ? (
        <div className="overflow-x-auto px-5 pb-1 pt-3">
          <table className="w-full border-collapse text-sm tabular-nums">
            <thead>
              <tr>
                {result.columns.map((c) => (
                  <th key={c.key} className={`border-b border-slate-200 py-2 text-xs font-medium text-slate-500 ${c.kind === 'dimension' ? 'text-left' : 'text-right'}`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.rows.map((row, i) => (
                <tr key={i}>
                  {result.columns.map((c) => (
                    <td key={c.key} className={`border-b border-slate-100 py-2 ${c.kind === 'dimension' ? 'text-left text-slate-900' : 'text-right text-slate-700'}`}>
                      {formatValue(row[c.key], c, result.currency)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {dim && result.rows.length > 1 ? (
              <tfoot>
                <tr>
                  {result.columns.map((c, j) => (
                    <td key={c.key} className={`py-2 font-semibold text-slate-900 ${c.kind === 'dimension' ? 'text-left' : 'text-right'}`}>
                      {j === 0 ? 'Total' : c.kind !== 'dimension' && isSummable(c.key) ? formatValue(columnTotal(result.rows, c), c, result.currency) : ''}
                    </td>
                  ))}
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      ) : (
        <p className="px-5 pb-2 pt-4 text-sm text-slate-500">Nothing was recorded in this period.</p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
        {saved ? null : naming && !save.isSuccess ? (
          <form
            className="flex flex-1 flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (title.trim()) save.mutate({ title: title.trim(), question: question ?? '', spec: { ...result.spec, chart } });
            }}
          >
            <input autoFocus className="input h-8 min-w-[12rem] flex-1 py-1 text-sm" maxLength={80} value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Report name" />
            <Button type="submit" size="sm" variant="primary" loading={save.isPending} disabled={!title.trim()}>
              Save
            </Button>
            <Button type="button" size="sm" onClick={() => setNaming(false)}>
              Cancel
            </Button>
          </form>
        ) : (
          <Button size="sm" disabled={save.isSuccess} onClick={() => setNaming(true)}>
            {save.isSuccess ? '★ Saved' : '☆ Save report'}
          </Button>
        )}
        {onFollowUp && !naming ? (
          <Button size="sm" onClick={onFollowUp}>
            Ask a follow-up
          </Button>
        ) : null}
        <span className="ml-auto text-xs text-slate-500">{saved ? 'Saved report · free' : '1 credit used'}</span>
        {save.isError ? <p className="w-full text-xs text-red-700">{getErrorMessage(save.error, 'Could not save this report.')}</p> : null}
      </div>
    </article>
  );
}
